import {
	Card,
	Descriptions,
	Tag,
	Button,
	Space,
	Typography,
	notification,
	Spin,
	Tabs,
	Table,
	Empty,
	Divider,
} from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { requestGetServiceGroupById, requestGetServiceList } from "../../services/admin.service";
import { PiArrowLeftDuotone, PiPencilDuotone, PiEyeDuotone } from "react-icons/pi";

const { Title, Text } = Typography;

function ViewServiceGroupPage() {
	const navigate = useNavigate();
	const { id } = useParams();
	const [loading, setLoading] = useState(true);
	const [serviceGroupData, setServiceGroupData] = useState(null);
	const [services, setServices] = useState([]);
	const [servicesLoading, setServicesLoading] = useState(false);

	useEffect(() => {
		document.title = "Cloudiva Console | View Service Group";
		if (id) {
			fetchServiceGroupData();
			fetchGroupServices();
		}
	}, [id]);

	const fetchServiceGroupData = () => {
		setLoading(true);
		requestGetServiceGroupById(id)
			.then((response) => {
				setServiceGroupData(response.data.data);
			})
			.catch(() => {
				notification.error({
					message: "Error",
					description: "Failed to fetch service group data",
				});
				navigate("/service-groups");
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const fetchGroupServices = () => {
		setServicesLoading(true);
		requestGetServiceList({
			limit: 100,
			filter: `groupId:${id}`,
			order: "name:asc",
		})
			.then((response) => {
				setServices(response.data.data);
			})
			.catch((error) => {
				console.error("Error fetching services:", error);
				notification.error({
					message: "Error",
					description: "Failed to fetch services for this group",
				});
			})
			.finally(() => {
				setServicesLoading(false);
			});
	};

	const serviceColumns = [
		{
			title: "Service Name",
			dataIndex: "name",
			key: "name",
			render: (text, record) => (
				<div>
					<div style={{ fontWeight: 500 }}>{text}</div>
					<div style={{ fontSize: "12px", color: "#666" }}>Code: {record.code}</div>
				</div>
			),
		},
		{
			title: "Description",
			dataIndex: "description",
			key: "description",
			render: (text) => text || "-",
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			render: (status) => (
				<Tag color={status === "ACTIVE" ? "green" : "red"}>
					{status === "ACTIVE" ? "Active" : "Inactive"}
				</Tag>
			),
		},
		{
			title: "Plans",
			dataIndex: "plans",
			key: "plans",
			render: (plans) => plans?.length || 0,
		},
		{
			title: "Actions",
			key: "actions",
			render: (_, record) => (
				<Space size="small">
					<Button
						type="text"
						size="small"
						icon={<PiEyeDuotone />}
						onClick={() => navigate(`/services/${record._id}`)}
						title="View Service"
					/>
					<Button
						type="text"
						size="small"
						icon={<PiPencilDuotone />}
						onClick={() => navigate(`/services/${record._id}/edit`)}
						title="Edit Service"
					/>
				</Space>
			),
		},
	];

	if (loading) {
		return (
			<div
				style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
				<Spin size="large" />
			</div>
		);
	}

	if (!serviceGroupData) {
		return (
			<div className="page-content">
				<div className="page-content-wrapper">
					<Card>
						<Empty description="Service group not found" />
					</Card>
				</div>
			</div>
		);
	}

	const tabItems = [
		{
			key: "1",
			label: "Basic Information",
			children: (
				<Card>
					<Descriptions column={2} bordered>
						<Descriptions.Item label="Service Group Code">
							<Text code>{serviceGroupData.code}</Text>
						</Descriptions.Item>
						<Descriptions.Item label="Service Group Name">
							<Text strong>{serviceGroupData.name}</Text>
						</Descriptions.Item>
						<Descriptions.Item label="Status">
							<Tag color={serviceGroupData.status === "ACTIVE" ? "green" : "red"}>
								{serviceGroupData.status === "ACTIVE" ? "Active" : "Inactive"}
							</Tag>
						</Descriptions.Item>
						<Descriptions.Item label="Created At">
							{new Date(serviceGroupData.createdAt).toLocaleString()}
						</Descriptions.Item>
						<Descriptions.Item label="Updated At">
							{new Date(serviceGroupData.updatedAt).toLocaleString()}
						</Descriptions.Item>
						<Descriptions.Item label="Services Count">{services.length}</Descriptions.Item>
						<Descriptions.Item label="Description" span={2}>
							{serviceGroupData.description || "No description provided"}
						</Descriptions.Item>
						{serviceGroupData.metadata && Object.keys(serviceGroupData.metadata).length > 0 && (
							<Descriptions.Item label="Metadata" span={2}>
								<pre style={{ margin: 0, fontSize: "12px" }}>
									{JSON.stringify(serviceGroupData.metadata, null, 2)}
								</pre>
							</Descriptions.Item>
						)}
					</Descriptions>
				</Card>
			),
		},
		{
			key: "2",
			label: `Services (${services.length})`,
			children: (
				<Card title="Services in this Group">
					{services.length > 0 ? (
						<Table
							columns={serviceColumns}
							dataSource={services}
							loading={servicesLoading}
							rowKey="_id"
							pagination={false}
						/>
					) : (
						<Empty description="No services found in this group" />
					)}
				</Card>
			),
		},
	];

	return (
		<div className="page-content">
			<div style={{ marginBottom: 24 }}>
				<Space split={<Divider type="vertical" />}>
					<Space>
						<Button icon={<PiArrowLeftDuotone />} onClick={() => navigate("/service-groups")} />
						<Title level={4} style={{ margin: 0 }}>
							{serviceGroupData?.name}
						</Title>
					</Space>
				</Space>
			</div>
			<div className="page-content-wrapper">
				<Card
					title={`Service Group Details - ${serviceGroupData.name}`}
					extra={
						<Space>
							<Button onClick={() => navigate("/service-groups")} style={{ marginRight: 8 }}>
								Back to List
							</Button>
							<Button
								type="primary"
								icon={<PiPencilDuotone />}
								onClick={() => navigate(`/service-groups/${serviceGroupData._id}/edit`)}>
								Edit
							</Button>
						</Space>
					}>
					<Tabs items={tabItems} />
				</Card>
			</div>
		</div>
	);
}

export default ViewServiceGroupPage;
